import DOC from '../../constants/DOC';
import get from '../accessors/get';
import throwError from '../error/_throwError';
import coreLogger from '../log/coreLogger';
import getScoped from './getScoped';

export interface ObjectKeyMap {
  [index: string]: string | string[] | ObjectKeyMap;
}

export interface FnKeyMap<Result, State, Props, ScopedState> {
  (state: State, props: Props, scopedState: ScopedState): Result;
}

type KepMap<Result, State, Props, ScopedState> = ObjectKeyMap | string | KepMap<Result, State, Props, ScopedState>[] | FnKeyMap<Result, State, Props, ScopedState>;

interface MapScopeToPropsProps {
  scope: string[];
}

/**
 * @since 0.19.0
 * @visibleName mapScopeToProps
 * @component
 */
export default function mapScopeToProps<
  Result extends Record<string, unknown>,
  State = any,
  Props = any,
  ScopedState = any
>(...keyMaps: KepMap<Result, State, Props, ScopedState>[]) {
  return function mapStateToProps(state: State, props: Props & MapScopeToPropsProps): Result {
    const { scope } = props;

    if (!scope) {
      throwError(`mapStateToProps: A "scope" prop could not be located. You may need to use "withScope" to inject the current scope. See ${DOC.DOC_URL}.`);
    }

    return keyMaps.reduce((result, keyMap) => {
      Object.assign(result, getState(keyMap));
      return result;
    }, {} as Result);

    function getState(keyMap: KepMap<Result, State, Props, ScopedState>) {
      if (typeof keyMap === 'string') {
        const prop = keyMap.split('.').pop().replace('/', '');
        return { [prop]: getScoped(state, keyMap, scope) };
      }

      if (Array.isArray(keyMap)) {
        return keyMap.reduce((result, path) => {
          Object.assign(result, getState(path));
          return result;
        }, {});
      }

      if (typeof keyMap === 'object') {
        return Object.keys(keyMap).reduce((result, prop) => {
          const path = keyMap[prop];
          if (typeof path === 'string') {
            (result as Record<string, unknown>)[prop] = getScoped(state, path, scope);
          } else if (Array.isArray(path)) {
            (result as Record<string, unknown>)[prop] = path.map((currentPath) => getScoped(state, currentPath, scope));
          } else {
            (result as Record<string, unknown>)[prop] = getState(path);
          }
          return result;
        }, {} as Result);
      }

      if (typeof keyMap === 'function') {
        const scopePath = scope.join('.');
        const scopedState = scope.length ? get(state, scopePath) as ScopedState : state as unknown as ScopedState;
        return keyMap(state, props, scopedState);
      }

      coreLogger.warn('mapScopeToProps: Invalid arguments.');

      return undefined;
    }
  };
}
